import _ from 'lodash';

import { ARRAY_FUNCTIONS_WITH_ITERATORS, LEX_TOKEN_TYPES } from 'constants/parser';
import { jsonStringify, parseJSONObject } from 'utils/json';

/**
 * Segments that name something on Object.prototype rather than a schema property. `_.get` resolves
 * them (`application.constructor.name` reads back as `'Object'`), so a path containing one typed as
 * a real field instead of reporting `Wrong path`.
 */
const NON_SCHEMA_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * The schema key a path segment names, matched case-insensitively.
 *
 * Null when nothing matches, and also when more than one key matches by case alone — two
 * properties differing only in casing cannot be told apart, and picking either would be a guess.
 */
const findSchemaKey = (container, segment) => {
  if (!_.isPlainObject(container) || NON_SCHEMA_KEYS.has(segment)) {
    return null;
  }

  if (Object.hasOwn(container, segment)) {
    return segment;
  }

  const matches = Object.keys(container).filter(key => _.toLower(key) === _.toLower(segment));

  return matches.length === 1 ? matches[0] : null;
};

/**
 * Resolve a dotted path against the entities schema, walking own properties only.
 *
 * The single schema-lookup implementation, shared by the lexer's type lookup and the validator's
 * path walk, so both agree on what the schema does and does not define.
 */
export const getSchemaValue = ({ entitiesSchema, path, defaultValue = '' }) => {
  const value = (path || '').split('.').reduce((container, segment) => {
    if (
      !_.isPlainObject(container) ||
      NON_SCHEMA_KEYS.has(segment) ||
      !Object.hasOwn(container, segment)
    ) {
      return;
    }

    return container[segment];
  }, entitiesSchema);

  return _.isUndefined(value) ? defaultValue : value;
};

/**
 * Re-spell a field path with the schema's own casing.
 *
 * Expression definitions ship PascalCase property names (`application.NmlsLookup`) while their
 * entity names are camelCase, and the engine binds members with IgnoreCase — so a hand-written or
 * cross-tenant path whose segment casing differs still evaluates server-side, but a strict lookup
 * reported it as a broken rule (STP-555). Casing is already tolerated for values (string `==` is
 * case-insensitive) and for node types; paths were the outlier.
 *
 * Canonicalizing rather than merely tolerating matters because the parsed tree is what gets saved,
 * and the backend's own validator matches property names ordinally.
 *
 * The first segment that does not resolve stops the rewrite and is left as authored, for the
 * validation walk to report. `arrayEntity` seeds the walk at an entity the path is scoped to,
 * mirroring the validator's own accumulator; a seed that is not an entity name resolves nothing,
 * exactly as the strict walk it replaces did.
 */
const walkFieldPath = ({ entitiesSchema, path, arrayEntity }) =>
  (path || '').split('.').reduce(
    (acc, segment) => {
      const withSegment = spelling => ({
        ...acc,
        canonicalSegments: [...acc.canonicalSegments, spelling],
      });

      if (acc.isStopped) {
        return withSegment(segment);
      }

      const container = acc.entityPath
        ? getSchemaValue({ entitiesSchema, path: acc.entityPath, defaultValue: {} })
        : entitiesSchema;
      const schemaKey = findSchemaKey(container, segment);

      if (!schemaKey) {
        return { ...withSegment(segment), isStopped: true };
      }

      const childPath = acc.entityPath ? `${acc.entityPath}.${schemaKey}` : schemaKey;
      const resolved = getSchemaValue({ entitiesSchema, path: childPath });

      return {
        ...withSegment(schemaKey),
        entityPath: _.isObject(resolved) ? childPath : resolved,
        resolved,
      };
    },
    { isStopped: false, entityPath: arrayEntity, canonicalSegments: [], resolved: undefined },
  );

export const canonicalizeFieldPath = ({ entitiesSchema, path, arrayEntity }) =>
  walkFieldPath({ entitiesSchema, path, arrayEntity }).canonicalSegments.join('.');

/**
 * The schema type a field path resolves to, following entity indirection.
 *
 * `getSchemaValue` stops at the first segment whose value is an entity NAME rather than a nested
 * object, so it cannot type `application.DeliveryChannel.SelectedChannels`; this follows the same
 * hops the path walk does.
 */
const getFieldSchemaType = ({ entitiesSchema, path, arrayEntity }) => {
  const { isStopped, resolved } = walkFieldPath({ entitiesSchema, path, arrayEntity });

  return isStopped ? null : resolved;
};

/** The entity an in-scope iterator ranges over, or undefined — own properties only, so a path
 *  rooted at `constructor` or `toString` cannot pick up an `Object.prototype` member. */
const getIteratorEntity = (iterators, name) =>
  Object.hasOwn(iterators, name) ? iterators[name] : undefined;

/**
 * The element entity an `array<channel>` / `array[channel]` schema type holds.
 *
 * A bare `array` carries no element entity, and must read as nullish rather than as an empty
 * name — callers fall through to another source with `??`, which an empty string would swallow.
 */
export const getArrayElementEntity = arrayType => {
  if (!_.isString(arrayType) || !arrayType.startsWith(LEX_TOKEN_TYPES.array)) {
    return null;
  }

  return arrayType.replace(LEX_TOKEN_TYPES.array, '').slice(1, -1) || null;
};

/**
 * The iterator an array function binds over its predicate, and the entity that iterator ranges over.
 *
 * `ANY(application.DeliveryChannel.SelectedChannels, ch, ch.Id == '…')` binds `ch` to the element
 * entity of the collection operand, so paths rooted at `ch` resolve against that entity rather than
 * against the schema roots.
 */
const getIteratorBinding = ({ entitiesSchema, node, iterators }) => {
  if (
    _.toLower(node.type) !== LEX_TOKEN_TYPES.function ||
    !ARRAY_FUNCTIONS_WITH_ITERATORS.includes(node.value)
  ) {
    return null;
  }

  const [collection, iterator] = node.operands || [];
  const iteratorName = iterator?.value;

  if (!iteratorName) {
    return null;
  }

  // The collection can itself be rooted at an enclosing iterator — ALL(application.Owners, o,
  // ANY(o.Addresses, a, …)) — so it resolves through the same hop a field path would.
  const [root, ...rest] = (collection?.value || '').split('.');
  const outerEntity = getIteratorEntity(iterators, root);
  const collectionType = outerEntity
    ? getFieldSchemaType({ entitiesSchema, path: rest.join('.'), arrayEntity: outerEntity })
    : getFieldSchemaType({ entitiesSchema, path: collection?.value });

  // A collection whose schema type names no element — a bare `array` — falls through to the type
  // the node itself carries, which may name one. ALL(WHERE(…), it, it.Field) is NOT that case and
  // binds nothing: the parser types a WHERE node as a bare `array` too, keeping the element entity
  // on an `arrayEntity` prop a stored tree never carries.
  const entity =
    getArrayElementEntity(collectionType) ?? getArrayElementEntity(collection?.typeOfValue);

  return entity ? { [iteratorName]: entity } : null;
};

/**
 * Re-spell every field path in a serialized `CriteriaExpressionItem` with the schema's casing.
 *
 * For the save paths that re-persist a stored tree verbatim instead of recompiling it from the
 * editor text — the TPO signup config drawer's DEV-3494 guard — where the parser never sees the
 * tree and so cannot canonicalize it (STP-573). Only `Field` node values change; node structure,
 * and with it the array-function iterator operands that guard exists to protect, is untouched.
 *
 * Paths rooted at an array function's iterator are re-spelled against the entity that iterator
 * ranges over. Those trees are most of what reaches this function — the preserved-tree branch
 * exists because array functions cannot round-trip — so leaving the lambda body alone would
 * canonicalize the collection while the comparison the rule actually decides on kept its casing.
 *
 * Returns the input unchanged when nothing was re-spelled, so an already-correct config is not
 * re-serialized. A string in gives a string back.
 */
export const canonicalizeExpressionFieldPaths = ({ entitiesSchema, expression }) => {
  if (!expression || !_.isPlainObject(entitiesSchema)) {
    return expression;
  }

  const tree = _.isString(expression) ? parseJSONObject(expression) : expression;

  if (_.isEmpty(tree)) {
    return expression;
  }

  const canonicalizeFieldValue = ({ path, iterators }) => {
    const [root, ...rest] = (path || '').split('.');
    const arrayEntity = getIteratorEntity(iterators, root);

    if (!arrayEntity) {
      return canonicalizeFieldPath({ entitiesSchema, path });
    }

    if (!rest.length) {
      return path;
    }

    return [
      root,
      canonicalizeFieldPath({ entitiesSchema, path: rest.join('.'), arrayEntity }),
    ].join('.');
  };

  const canonicalizeNode = (node, iterators) => {
    if (!_.isPlainObject(node)) {
      return { node, isChanged: false };
    }

    const scopedIterators = {
      ...iterators,
      ...getIteratorBinding({ entitiesSchema, node, iterators }),
    };
    const operands = _.isArray(node.operands) ? node.operands : [];
    const operandResults = operands.map(operand => canonicalizeNode(operand, scopedIterators));
    const isOperandChanged = operandResults.some(({ isChanged }) => isChanged);

    const isField = _.isString(node.value) && _.toLower(node.type) === LEX_TOKEN_TYPES.field;
    const value = isField ? canonicalizeFieldValue({ path: node.value, iterators }) : node.value;
    const isValueChanged = value !== node.value;

    if (!isValueChanged && !isOperandChanged) {
      return { node, isChanged: false };
    }

    return {
      node: {
        ...node,
        ...(isValueChanged && { value }),
        ...(_.isArray(node.operands) && { operands: operandResults.map(result => result.node) }),
      },
      isChanged: true,
    };
  };

  const { node, isChanged } = canonicalizeNode(tree, {});

  if (!isChanged) {
    return expression;
  }

  return _.isString(expression) ? jsonStringify(node) : node;
};
